import React, {
  createContext,
  useMemo,
  type ReactNode,
  useContext,
  useState,
} from "react";

import { isNil } from "lodash";

import {
  type ApolloQueryResult,
  type OperationVariables,
} from "@apollo/client";
import { LoadingSlide } from "@components/title-slide-view";
import {
  CREATE_CUSTOM_METRIC,
  DELETE_CUSTOM_METRIC,
  GET_CUSTOM_METRICS,
  UPDATE_CUSTOM_METRIC,
} from "@gql/custom-metrics";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type GetCustomMetrics_me_customMetrics,
  type GetCustomMetrics,
  type CreateCustomMetric,
  type CreateCustomMetricVariables,
  type DeleteCustomMetric,
  type DeleteCustomMetricVariables,
  type UpdateCustomMetricVariables,
  type UpdateCustomMetric,
} from "@north-beam/nb-client-side";

import { BareCustomMetrics } from "../custom-metrics-modal";

export interface CustomMetricsState {
  readonly customMetrics: GetCustomMetrics_me_customMetrics[];
  readonly loadingCustomMetrics: boolean;

  refetch: (
    variables?: Partial<OperationVariables> | undefined,
  ) => Promise<ApolloQueryResult<GetCustomMetrics>>;
  createCustomMetric: Awaited<
    ReturnType<
      typeof useNorthbeamMutation<
        CreateCustomMetric,
        CreateCustomMetricVariables
      >
    >
  >[0];
  updateCustomMetric: Awaited<
    ReturnType<
      typeof useNorthbeamMutation<
        UpdateCustomMetric,
        UpdateCustomMetricVariables
      >
    >
  >[0];
  deleteCustomMetric: Awaited<
    ReturnType<
      typeof useNorthbeamMutation<
        DeleteCustomMetric,
        DeleteCustomMetricVariables
      >
    >
  >[0];
  readonly loadingState: {
    isCreateMetricLoading: boolean;
    loadingCustomMetrics: boolean;
    isUpdateMetricLoading: boolean;
    isDeleteMetricLoading: boolean;
  };
  modalIsOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

export const CustomMetricsContext = createContext<CustomMetricsState | null>(
  null,
);

const customMetricQueryOptions = {
  refetchQueries: [{ query: GET_CUSTOM_METRICS }],
  awaitRefetchQueries: true,
};

export const CustomMetricsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    data,
    loading: loadingCustomMetrics,
    refetch,
  } = useNorthbeamQuery<GetCustomMetrics>(GET_CUSTOM_METRICS, {
    notifyOnNetworkStatusChange: false,
  });

  const [createCustomMetric, { loading: isCreateMetricLoading }] =
    useNorthbeamMutation<CreateCustomMetric, CreateCustomMetricVariables>(
      CREATE_CUSTOM_METRIC,
      customMetricQueryOptions,
    );
  const [updateCustomMetric, { loading: isUpdateMetricLoading }] =
    useNorthbeamMutation<UpdateCustomMetric, UpdateCustomMetricVariables>(
      UPDATE_CUSTOM_METRIC,
      customMetricQueryOptions,
    );
  const [deleteCustomMetric, { loading: isDeleteMetricLoading }] =
    useNorthbeamMutation<DeleteCustomMetric, DeleteCustomMetricVariables>(
      DELETE_CUSTOM_METRIC,
      customMetricQueryOptions,
    );

  const customMetrics = useMemo(() => data?.me?.customMetrics ?? [], [data]);

  return (
    <CustomMetricsContext.Provider
      value={{
        customMetrics,
        loadingCustomMetrics,
        refetch,
        createCustomMetric,
        updateCustomMetric,
        deleteCustomMetric,
        loadingState: {
          isCreateMetricLoading,
          loadingCustomMetrics,
          isUpdateMetricLoading,
          isDeleteMetricLoading,
        },
        modalIsOpen: isOpen,
        closeModal: () => setIsOpen(false),
        openModal: () => setIsOpen(true),
      }}
    >
      {(data || !loadingCustomMetrics) && children}
      {loadingCustomMetrics && !data && (
        <div className="h-[calc(100vh-56px)]">
          <LoadingSlide />
        </div>
      )}
      {isOpen && (
        <BareCustomMetrics isOpen={isOpen} onClose={() => setIsOpen(false)} />
      )}
    </CustomMetricsContext.Provider>
  );
};

export const useCustomMetricsContext = () => {
  const context = useContext(CustomMetricsContext);
  if (isNil(context)) {
    throw new Error(
      "useCustomMetricsContext must be used within a CustomMetricsProvider",
    );
  }

  return context;
};
